// @flow

import * as React from 'react';
import {ContentGroupItem} from 'docs/util';
import type {CurrenciesContext} from 'docs/types';
import {defineMessages, FormattedMessage} from 'react-intl';
import {Callout} from 'docs/components';
import {List} from '../../core/List/List';
import {ListItem} from '../../core/List/ListItem';
import {TabGroup} from '../../core/Tabs/TabGroup';
import {InlineCode} from '../../core/InlineCode/InlineCode';

const messages = defineMessages({
  noAmexSupport: {
    id: 'docsShell.supportedPresentmentCurrencies.noAmexSupport',
    defaultMessage:
      'Currencies marked with <InlineCode>*</InlineCode> are not supported by American Express',
  },
  canOnlyUseJPY: {
    id: 'docsShell.supportedPresentmentCurrencies.canOnlyUseJPY',
    defaultMessage:
      'JCB, Diners Club, and Discover cards can only be charged in JPY',
  },
  amexMXN: {
    id: 'docsShell.supportedPresentmentCurrencies.amexMXN',
    defaultMessage: 'You can charge American Express cards only in MXN',
  },
  canOnlyUseBRL: {
    id: 'docsShell.supportedPresentmentCurrencies.canOnlyUseBRL',
    defaultMessage: 'You can only charge Brazilian cards in BRL',
  },
  domesticIN: {
    id: 'docsShell.supportedPresentmentCurrencies.domesticIN',
    defaultMessage: 'Domestic charges can only be in INR',
  },
  internationalIN: {
    id: 'docsShell.supportedPresentmentCurrencies.internationalIN',
    defaultMessage:
      'International payments are allowed only in non-INR currencies',
  },
  tiedToCardPayments: {
    id: 'docsShell.supportedPresentmentCurrencies.tiedToCardPayments',
    defaultMessage:
      'These currencies apply to card payments: other payment methods are often tied to a specific currency',
  },
  followingExceptions: {
    id: 'docsShell.supportedPresentmentCurrencies.followingExceptions',
    defaultMessage:
      'Stripe users can process charges in the following list of currencies with these exceptions:',
  },
  indiaAmexCallout: {
    id: 'docsShell.supportedPresentmentCurrencies.indiaAmexCallout',
    description:
      'A warning presented to merchants in India about AMEX onboarding restrictions',
    defaultMessage:
      'The <a1>Reserve Bank of India</a1> has directed American Express to stop on-boarding new domestic customers from India onto their card networks from May 1, 2021. To stay compliant with these requirements, we will not offer American Express card processing on Stripe India accounts on-boarded to Stripe from May 1, 2021 onwards. Learn more on our <a2>support page</a2>. If you on-boarded to Stripe prior to May 1, 2021 you can charge American Express cards only in INR and USD.',
  },
});

type SupportedPresentmentCurrenciesProps = {|
  currencies: CurrenciesContext,
  children?: React.Node,
|};

export const SupportedPresentmentCurrencies = ({
  currencies,
  children,
}: SupportedPresentmentCurrenciesProps) => {
  if (!currencies) {
    return null;
  }

  const items = currencies.supported_presentment_currencies.map((entry) => {
    const noAmex = ['MX', 'IN'].includes(entry.country)
      ? []
      : currencies.no_amex_support;

    const exceptions = (
      <>
        <p>
          <FormattedMessage {...messages.followingExceptions} />
        </p>
        <List>
          {entry.country === 'JP' && (
            <ListItem>
              <FormattedMessage {...messages.canOnlyUseJPY} />
            </ListItem>
          )}

          {entry.country === 'MX' && (
            <ListItem>
              <FormattedMessage {...messages.amexMXN} />
            </ListItem>
          )}

          {entry.country === 'BR' && (
            <ListItem>
              <FormattedMessage {...messages.canOnlyUseBRL} />
            </ListItem>
          )}

          {entry.country === 'IN' && (
            <>
              <Callout type="warning">
                <FormattedMessage
                  {...messages.indiaAmexCallout}
                  values={{
                    a1: (str) => (
                      <a
                        href="https://www.rbi.org.in/Scripts/BS_PressReleaseDisplay.aspx?prid=51471"
                        target="_blank"
                      >
                        {str}
                      </a>
                    ),
                    a2: (str) => (
                      <a
                        href="https://support.stripe.com/questions/new-rbi-restrictions-on-american-express-onboarding-for-indian-merchants"
                        target="_blank"
                      >
                        {str}
                      </a>
                    ),
                  }}
                />
              </Callout>
              <ListItem>
                <FormattedMessage {...messages.domesticIN} />
              </ListItem>
              <ListItem>
                <FormattedMessage {...messages.internationalIN} />
              </ListItem>
            </>
          )}

          {noAmex.length > 0 && (
            <ListItem>
              <FormattedMessage
                {...messages.noAmexSupport}
                values={{
                  InlineCode: (label) => <InlineCode>{label}</InlineCode>,
                }}
              />
            </ListItem>
          )}

          <ListItem>
            <FormattedMessage {...messages.tiedToCardPayments} />
          </ListItem>
        </List>
      </>
    );

    const currencyList = (
      <List columns={2}>
        {entry.currencies?.map((currency) => {
          const name = currency.toUpperCase();
          return (
            <ListItem>
              <>
                {currencies.zero_decimal.includes(currency) ? (
                  <a href="#zero-decimal">{name}</a>
                ) : (
                  name
                )}
                {noAmex.includes(currency) && '*'}
              </>
            </ListItem>
          );
        })}
      </List>
    );

    const content = [exceptions, children, currencyList];
    return new ContentGroupItem(
      entry.country,
      entry.country_name || '',
      content,
    );
  });

  return (
    <TabGroup pref="presentment-currency" items={items} selector="drop-down" />
  );
};
